import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getBatchDetail, createBatch, updateBatch } from '../api/batchesApi';
import { appToast } from '../../../../components/AppToast';

export function useBatchesEntryScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editingBatchId = searchParams.get('id');

  const [form, setForm] = useState({
    name: '',
    short_name: '',
  });

  const [operationalStatus, setOperationalStatus] = useState('active');
  const [isLoading, setIsLoading] = useState(Boolean(editingBatchId));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load existing batch if editing
  useEffect(() => {
    async function loadIfEditing() {
      if (!editingBatchId) return;
      setIsLoading(true);
      setError('');
      try {
        const detail = await getBatchDetail(editingBatchId);
        if (detail) {
          setForm({
            name: detail.name || '',
            short_name: detail.short_name || '',
          });
          setOperationalStatus(detail.status === 'Active' ? 'active' : 'inactive');
        } else {
          setError('Batch not found.');
        }
      } catch (e) {
        const msg = e?.response?.data?.detail || 'Failed to load batch details.';
        setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      } finally {
        setIsLoading(false);
      }
    }
    loadIfEditing();
  }, [editingBatchId]);

  const patchForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Batch Name is required.');
      appToast.error('Batch Name is required.');
      return;
    }
    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        name: form.name.trim(),
        short_name: form.short_name ? form.short_name.trim() : null,
        status: operationalStatus === 'active' ? 'Active' : 'Archive',
      };

      if (editingBatchId) {
        await updateBatch(editingBatchId, payload);
        appToast.success('Batch updated successfully.');
        setSuccess('Batch updated successfully.');
      } else {
        await createBatch(payload);
        appToast.success('Batch created successfully.');
        setSuccess('Batch created successfully.');
      }

      setTimeout(() => {
        navigate('/masters/batches/list');
      }, 600);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to save batch.';
      const errStr = typeof msg === 'string' ? msg : JSON.stringify(msg);
      setError(errStr);
      appToast.error(errStr);
    } finally {
      setIsSaving(false);
    }
  };

  const completionPercent = useMemo(() => {
    const fields = ['name', 'short_name'];
    const filled = fields.filter((f) => String(form[f] || '').trim().length > 0).length;
    return Math.round((filled / fields.length) * 100);
  }, [form]);

  return {
    state: {
      form,
      operationalStatus,
      isLoading,
      isSaving,
      error,
      success,
      isEditMode: Boolean(editingBatchId),
      completionPercent,
    },
    actions: {
      patchForm,
      setOperationalStatus,
      handleSave,
    },
  };
}
